import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CubeIcon, TagIcon, BuildingOfficeIcon, EnvelopeIcon, PhotoIcon, EyeIcon } from '@heroicons/react/24/outline';
import API from '../api';
import { ADMIN_BASE } from '../utils/adminBase';

function StatCard({ label, value, icon: Icon, to, color }) {
  return (
    <Link to={to} className="bg-white border border-gray-100 shadow-sm p-5 flex items-center gap-4 hover:shadow-md transition-shadow group">
      <div className={`p-3 rounded ${color}`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <div>
        <div className="text-2xl font-bold text-gray-800">{value ?? '–'}</div>
        <div className="text-xs text-gray-500 font-medium uppercase tracking-wider">{label}</div>
      </div>
    </Link>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState({});
  const [views, setViews] = useState({ totalViews: 0, uniqueVisitors: 0, topCategories: [], topProducts: [] });

  useEffect(() => {
    Promise.all([
      API.get('/products/all'),
      API.get('/categories/all'),
      API.get('/clients/all'),
      API.get('/contact'),
      API.get('/hero/all'),
    ]).then(([p, c, m, msg, h]) => {
      setStats({
        products: p.data.length,
        categories: c.data.length,
        clients: m.data.length,
        messages: msg.data.length,
        unread: msg.data.filter(x => !x.is_read).length,
        slides: h.data.length,
      });
    }).catch(() => {});
    API.get('/views/summary').then(r => setViews(r.data)).catch(() => {});
  }, []);

  const cards = [
    { label: 'Products', value: stats.products, icon: CubeIcon, to: `${ADMIN_BASE}/products`, color: 'bg-primary' },
    { label: 'Categories', value: stats.categories, icon: TagIcon, to: `${ADMIN_BASE}/categories`, color: 'bg-blue-500' },
    { label: 'Clients', value: stats.clients, icon: BuildingOfficeIcon, to: `${ADMIN_BASE}/clients`, color: 'bg-emerald-600' },
    { label: 'Hero Slides', value: stats.slides, icon: PhotoIcon, to: `${ADMIN_BASE}/hero`, color: 'bg-purple-600' },
    { label: 'Messages', value: stats.messages, icon: EnvelopeIcon, to: `${ADMIN_BASE}/messages`, color: 'bg-amber-500' },
    { label: 'Page Views', value: views.totalViews, icon: EyeIcon, to: `${ADMIN_BASE}/categories`, color: 'bg-rose-500' },
    { label: 'Unique Visitors', value: views.uniqueVisitors, icon: EyeIcon, to: `${ADMIN_BASE}/categories`, color: 'bg-teal-600' },
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-display font-bold text-primary-dark">Dashboard</h1>
        <p className="text-sm text-gray-500 mt-1">Overview of your website content</p>
      </div>

      {stats.unread > 0 && (
        <div className="mb-6 bg-amber-50 border border-amber-200 px-4 py-3 flex items-center justify-between">
          <p className="text-sm text-amber-800">You have <strong>{stats.unread}</strong> unread message{stats.unread > 1 ? 's' : ''}.</p>
          <Link to={`${ADMIN_BASE}/messages`} className="text-xs font-semibold text-amber-700 hover:text-amber-900 underline">View Messages</Link>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-10">
        {cards.map(c => <StatCard key={c.label} {...c} />)}
      </div>

      {/* Most viewed */}
      {(views.topCategories.length > 0 || views.topProducts.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
          <div className="bg-white border border-gray-100 shadow-sm p-6">
            <h2 className="font-semibold text-gray-700 mb-1 text-sm uppercase tracking-wider">Most Viewed Categories</h2>
            <p className="text-xs text-gray-400 mb-3">Unique visitors (total views)</p>
            {views.topCategories.length === 0 ? (
              <p className="text-sm text-gray-400">No category views yet.</p>
            ) : (
              <ul className="space-y-2.5">
                {views.topCategories.map((c, i) => (
                  <li key={c.id} className="flex items-center justify-between text-sm">
                    <span className="text-gray-700 truncate pr-4"><span className="text-gray-400 mr-2">{i + 1}.</span>{c.name}</span>
                    <span className="text-primary font-semibold shrink-0">{c.unique_views} <span className="text-gray-400 font-normal">({c.views})</span></span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="bg-white border border-gray-100 shadow-sm p-6">
            <h2 className="font-semibold text-gray-700 mb-1 text-sm uppercase tracking-wider">Most Viewed Products</h2>
            <p className="text-xs text-gray-400 mb-3">Unique visitors (total views)</p>
            {views.topProducts.length === 0 ? (
              <p className="text-sm text-gray-400">No product views yet.</p>
            ) : (
              <ul className="space-y-2.5">
                {views.topProducts.map((p, i) => (
                  <li key={p.id} className="flex items-center justify-between text-sm">
                    <span className="text-gray-700 truncate pr-4"><span className="text-gray-400 mr-2">{i + 1}.</span>{p.name}</span>
                    <span className="text-primary font-semibold shrink-0">{p.unique_views} <span className="text-gray-400 font-normal">({p.views})</span></span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Quick links */}
      <div className="bg-white border border-gray-100 shadow-sm p-6">
        <h2 className="font-semibold text-gray-700 mb-4 text-sm uppercase tracking-wider">Quick Actions</h2>
        <div className="flex flex-wrap gap-3">
          {[
            [`${ADMIN_BASE}/products`, 'Add Product'],
            [`${ADMIN_BASE}/categories`, 'Add Category'],
            [`${ADMIN_BASE}/clients`, 'Add Client'],
            [`${ADMIN_BASE}/hero`, 'Edit Hero Slides'],
            [`${ADMIN_BASE}/settings`, 'Edit Site Settings'],
            [`${ADMIN_BASE}/messages`, 'View Messages'],
          ].map(([to, label]) => (
            <Link key={to} to={to} className="btn-outline text-xs py-1.5 px-4">{label}</Link>
          ))}
        </div>
      </div>
    </div>
  );
}
